import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useAuth } from "./auth";
import { useUserOrganizations } from "./org";
import { useTenantIdentity } from "./useTenantIdentity";

// Phase 6.11.2 hardening - resilient tenant linking.
//
// Linking happens on "first authenticated tenant load anywhere": whenever an
// authenticated user has NO org membership and NO linked tenant, the
// tenant-portal 'link' action is called once per user per session. The Edge
// Function is idempotent and race-guarded; with no pending invite it no-ops
// with a 4xx we ignore. The org-membership gate ensures this NEVER fires for
// managers. Mounted in RoleHome.
//
// RACE FIX (backported from useOwnerAutoLink, 6.12.2 acceptance testing):
// inFlight previously rose only inside the effect, one commit AFTER RoleHome
// had already rendered <Navigate> on the render where the last identity
// query settled. The 6.11 tests passed on query settle-order luck. The hold
// condition (`eligible && not yet settled for this user`) is now DERIVED
// DURING RENDER, so RoleHome holds the loading screen in the very render
// where the queries settle, before any effect runs.

export type AutoLinkState = {
  inFlight: boolean;
};

export function useTenantAutoLink(): AutoLinkState {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const {
    data: memberships,
    isSuccess: orgsLoaded,
  } = useUserOrganizations();
  const {
    data: tenant,
    isSuccess: tenantLoaded,
  } = useTenantIdentity();

  const attemptedFor = useRef<string | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [settledFor, setSettledFor] = useState<string | null>(null);

  // Computed at render time. True means: a link attempt is warranted for
  // this user and its outcome is not yet known, so RoleHome must not route.
  const eligible =
    !!user &&
    orgsLoaded &&
    tenantLoaded &&
    (memberships?.length ?? 0) === 0 &&   // a manager, not a tenant
    !tenant;                               // not already linked

  useEffect(() => {
    if (!eligible || !user) return;
    if (attemptedFor.current === user.id) return;   // once per user
    attemptedFor.current = user.id;
    setInFlight(true);

    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) return;

        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tenant-portal`;
        await fetch(url, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "link" }),
        });
      } catch {
        // Network/transient errors: fall through to the refetch below.
      } finally {
        // Refetch identity REGARDLESS of link outcome, before releasing the
        // hold. The cached identity may be stale from a prior session in the
        // same window (sign-out clears the Supabase session, not the React
        // Query cache), and an already-linked user gets a non-2xx from the
        // idempotency guard while their identity row is perfectly valid.
        await queryClient.invalidateQueries({ queryKey: ["tenant-identity"] });
        setInFlight(false);
        setSettledFor(user.id);
      }
    })();
  }, [eligible, user, queryClient]);

  return {
    inFlight: inFlight || (eligible && settledFor !== (user?.id ?? null)),
  };
}
